import { motion } from "framer-motion";
import { 
  TrendingUp, 
  Handshake, 
  Shield, 
  DollarSign, 
  Laptop, 
  FileText, 
  Search, 
  Users, 
  CheckCircle, 
  Leaf, 
  Building, 
  Wallet,
  ChevronRight,
  Sparkles,
  Globe
} from "lucide-react";

export default function AdvisoryServices() {
  const services = [
    {
      id: 1,
      title: "Strategy & Management Consulting",
      icon: TrendingUp,
      image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&q=80",
      items: [
        "Corporate strategy and business transformation",
        "Growth strategy and market entry advisory",
        "Operating model design",
        "Cost optimization and performance improvement",
        "Digital strategy and innovation advisory",
        "Customer strategy and experience transformation"
      ]
    },
    {
      id: 2,
      title: "Deals, M&A & Corporate Finance Advisory",
      icon: Handshake,
      image: "https://images.unsplash.com/photo-1556761175-b413da4baf72?w=800&q=80",
      items: [
        "Mergers, acquisitions, divestments, and carve-outs",
        "Buy-side and sell-side advisory",
        "Financial, commercial, operational, and tax due diligence",
        "Valuation and business modeling",
        "Post-merger integration (PMI) and separation advisory",
        "Joint ventures, strategic alliances, and partnerships",
        "Capital raising and restructuring advisory"
      ]
    },
    {
      id: 3,
      title: "Risk Advisory & Internal Audit",
      icon: Shield,
      image: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&q=80",
      items: [
        "Enterprise Risk Management (ERM)",
        "Internal audit and SOX compliance",
        "Regulatory risk and compliance advisory",
        "Operational and financial risk assessment",
        "Governance, risk, and compliance (GRC) frameworks",
        "Fraud risk management and investigations",
        "Third-party risk management"
      ]
    },
    {
      id: 4,
      title: "Financial Advisory & Restructuring",
      icon: DollarSign,
      image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80",
      items: [
        "Turnaround and restructuring advisory",
        "Insolvency and bankruptcy support",
        "Liquidity and cash flow management",
        "Distressed asset advisory",
        "Independent business reviews (IBR)",
        "Debt restructuring and creditor advisory"
      ]
    },
    {
      id: 5,
      title: "Technology & Digital Advisory",
      icon: Laptop,
      image: "https://images.unsplash.com/photo-1518770660439-4636190af475?w=800&q=80",
      items: [
        "IT strategy and transformation",
        "ERP implementation advisory (SAP, Oracle, Workday, etc.)",
        "Data analytics and business intelligence",
        "Cloud transformation advisory",
        "Cybersecurity and information security",
        "Artificial intelligence and automation advisory",
        "IT governance and controls"
      ]
    },
    {
      id: 6,
      title: "Tax Advisory Services",
      icon: FileText,
      image: "https://images.unsplash.com/photo-1554224311-beee2091d292?w=800&q=80",
      subsections: [
        {
          subtitle: "Domestic & Corporate Tax",
          items: [
            "Corporate tax planning and compliance",
            "Tax accounting and reporting",
            "Transaction tax advisory",
            "Tax controversy and dispute resolution"
          ]
        },
        {
          subtitle: "International & Global Tax",
          items: [
            "International tax structuring",
            "Cross-border transaction advisory",
            "Transfer pricing documentation and advisory",
            "BEPS, Pillar One & Pillar Two advisory",
            "DTAA and withholding tax advisory"
          ]
        },
        {
          subtitle: "Indirect Tax",
          items: [
            "GST / VAT / Sales tax advisory",
            "Customs and trade advisory",
            "Supply chain tax optimization"
          ]
        }
      ]
    },
    {
      id: 7,
      title: "Forensic & Investigation Services",
      icon: Search,
      image: "https://images.unsplash.com/photo-1507679799987-c73779587ccf?w=800&q=80",
      items: [
        "Fraud investigations",
        "Forensic accounting and dispute advisory",
        "Litigation support and expert testimony",
        "Anti-bribery and corruption compliance",
        "Regulatory investigations support",
        "Financial crime risk advisory"
      ]
    },
    {
      id: 8,
      title: "Human Capital & Workforce Advisory",
      icon: Users,
      image: "https://images.unsplash.com/photo-1521791136064-7986c2920216?w=800&q=80",
      items: [
        "Organization design and restructuring",
        "Workforce strategy and planning",
        "Change management and transformation",
        "Executive compensation and rewards advisory",
        "HR technology and digital HR advisory",
        "Learning, development, and leadership advisory"
      ]
    },
    {
      id: 9,
      title: "Regulatory & Compliance Advisory",
      icon: CheckCircle,
      image: "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=800&q=80",
      items: [
        "Financial services regulatory advisory",
        "Banking and capital markets compliance",
        "Insurance and asset management regulatory advisory",
        "ESG and sustainability reporting advisory",
        "Data privacy and regulatory compliance",
        "Licensing and regulatory approval support"
      ]
    },
    {
      id: 10,
      title: "ESG, Sustainability & Climate Advisory",
      icon: Leaf,
      image: "https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=800&q=80",
      items: [
        "ESG strategy and roadmap development",
        "Sustainability reporting (GRI, SASB, TCFD, CSRD)",
        "Climate risk and decarbonization advisory",
        "Responsible investment and ESG integration",
        "Impact measurement and reporting"
      ]
    },
    {
      id: 11,
      title: "Government & Public Sector Advisory",
      icon: Building,
      image: "https://images.unsplash.com/photo-1555421689-491a97ff2040?w=800&q=80",
      items: [
        "Public policy and governance advisory",
        "Government transformation and reform advisory",
        "Infrastructure and PPP advisory",
        "Public finance and budgeting advisory",
        "Procurement and tender advisory",
        "Regulatory framework development"
      ]
    },
    {
      id: 12,
      title: "Wealth Management & Private Client Advisory",
      icon: Wallet,
      image: "https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=800&q=80",
      badge: "Selective Offering",
      items: [
        "Private client and family office advisory",
        "Succession and estate planning",
        "Trust and fiduciary advisory",
        "Tax and wealth structuring for HNIs",
        "Cross-border wealth and residency planning"
      ]
    }
  ];

  const stats = [
    { number: "12+", label: "Service Lines" },
    { number: "100+", label: "Expert Advisors" },
    { number: "500+", label: "Projects Delivered" },
    { number: "50+", label: "Industry Sectors" }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-yellow-50 via-white to-yellow-50 border-b-2 border-yellow-200 overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 left-20 w-96 h-96 bg-yellow-500 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-yellow-400 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 py-24">
          <motion.div
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="flex justify-center mb-8">
              <div className="p-5 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full shadow-2xl">
                <Globe className="w-20 h-20 text-white" />
              </div>
            </div>
            
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-black mb-6 leading-tight">
              Our Advisory & <br />
              <span className="text-yellow-600">Consulting Services</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-600 max-w-4xl mx-auto mb-8">
              Comprehensive solutions to drive growth, manage risk, and transform your business for lasting success
            </p>
            
            <div className="flex justify-center">
              <button className="px-10 py-4 bg-gradient-to-r from-yellow-500 to-yellow-600 text-white font-bold text-lg rounded-xl hover:from-yellow-600 hover:to-yellow-700 transform hover:scale-105 transition-all duration-300 shadow-xl flex items-center space-x-2">
                <span>Explore Our Services</span>
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <motion.section
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        className="bg-gradient-to-r from-yellow-500 via-yellow-600 to-yellow-500 py-16"
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-4xl md:text-5xl font-bold text-white mb-2">
                  {stat.number}
                </div>
                <div className="text-yellow-100 text-sm md:text-base font-medium">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Services Grid */}
      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.05 }}
        variants={containerVariants}
        className="max-w-7xl mx-auto px-6 py-20"
      >
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center space-x-2 bg-yellow-100 px-4 py-2 rounded-full mb-4"
          >
            <Sparkles className="w-5 h-5 text-yellow-600" />
            <span className="text-yellow-700 font-semibold">12 Core Service Lines</span>
          </motion.div>
          <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">
            Comprehensive Advisory Solutions
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            From strategy to execution, we deliver integrated solutions across all business functions
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service) => {
            const IconComponent = service.icon;
            return (
              <motion.div
                key={service.id}
                variants={itemVariants}
                className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group border-2 border-gray-100 hover:border-yellow-400"
              >
                {/* Image Section */}
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent"></div>
                  
                  {/* Icon Badge */}
                  <div className="absolute top-4 right-4">
                    <div className="p-3 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-xl shadow-lg">
                      <IconComponent className="w-6 h-6 text-white" />
                    </div>
                  </div>

                  {/* Optional Badge */}
                  {service.badge && (
                    <div className="absolute top-4 left-4">
                      <div className="bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full">
                        <span className="text-yellow-700 text-xs font-bold">{service.badge}</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Title */}
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-xl font-bold text-white leading-tight">
                      {service.title}
                    </h3>
                  </div>
                </div>

                {/* Content Section */}
                <div className="p-6">
                  {service.subsections ? (
                    // Tax Services with subsections
                    <div className="space-y-4">
                      {service.subsections.map((subsection, subIdx) => (
                        <div key={subIdx}>
                          <h4 className="text-sm font-bold text-yellow-600 mb-2">
                            {subsection.subtitle}
                          </h4>
                          <ul className="space-y-2">
                            {subsection.items.map((item, idx) => (
                              <li key={idx} className="flex items-start space-x-2">
                                <ChevronRight className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                                <span className="text-gray-700 text-sm leading-relaxed">
                                  {item}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  ) : (
                    // Regular services
                    <ul className="space-y-2.5">
                      {service.items.map((item, idx) => (
                        <li key={idx} className="flex items-start space-x-2.5 group/item">
                          <ChevronRight className="w-5 h-5 text-yellow-500 mt-0.5 flex-shrink-0 group-hover/item:translate-x-1 transition-transform" />
                          <span className="text-gray-700 text-sm leading-relaxed">
                            {item}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Bottom Accent */}
                <div className="px-6 pb-6">
                  <div className="h-1.5 bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 rounded-full transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </motion.section>

      {/* CTA Section */}
      <motion.section
        initial={{ opacity: 0, scale: 0.95 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        className="relative bg-gradient-to-r from-yellow-500 via-yellow-600 to-yellow-500 text-white py-24 overflow-hidden"
      >
        {/* Decorative Elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-6xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            Ready to Transform Your Business?
          </h2>
          <p className="text-xl md:text-2xl mb-10 text-yellow-50 max-w-3xl mx-auto">
            Our expert advisors are ready to help you navigate complex challenges and seize new opportunities
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="px-10 py-4 bg-white text-yellow-600 font-bold text-lg rounded-xl hover:bg-gray-100 transform hover:scale-105 transition-all duration-300 shadow-2xl">
              Schedule a Consultation
            </button>
            <button className="px-10 py-4 bg-transparent border-2 border-white text-white font-bold text-lg rounded-xl hover:bg-white hover:text-yellow-600 transform hover:scale-105 transition-all duration-300">
              Download Service Brochure
            </button>
          </div>
        </div>
      </motion.section>
    </div>
  );
}
